import type { RefObject } from 'react';
import { classNames } from 'primereact/utils';
import { ProgressSpinner } from '@repo/atomic-components';
import type { ChatMessage as ChatMessageType, Citation, DraftInfo, MessageAttachedDocument } from '@/types/chat';
import type { FileStatus } from '@/types/file';
import { ChatMessageList } from './ChatMessageList';
import { ScrollToRefButton } from './ScrollToRefButton';
import { getChatThinkingLabel } from '../utils/getChatThinkingLabel';
import { getLatestAssistantMessage } from '../utils/getLatestAssistantMessage';
import { useIsStreamedTextIdle } from '../hooks/useIsStreamedTextIdle';

export type ChatScrollableMessageAreaProps = {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  messageRefs: RefObject<Map<string, HTMLDivElement | null>>;
  onResumeScrollToBottom: () => void;
  hasMessages: boolean;
  searchQuery: string;
  messages: ChatMessageType[];
  onCitationClick: (citation: Citation) => void;
  globalOrderedUuids: string[];
  chatCitations: Record<string, Citation>;
  isLoading: boolean;
  isWaitingForDocumentProcessing?: boolean;
  documentsByMessageId?: Map<string, MessageAttachedDocument[]>;
  promptAttachmentStatusById?: Map<string, FileStatus>;
  onAttachedDocumentClick?: (documentId: string) => void;
  canEditChat: boolean;
  onDraftClick?: (draft: DraftInfo) => void;
  onEditPrompt?: (prompt: string) => void;
  onExpandChatInput?: () => void;
  documentNameById?: Map<string, string>;
  /** Pads the end of the transcript so the last message can scroll clear of the floating chat button. */
  clearsFloatingChatButton?: boolean;
};

export function ChatScrollableMessageArea({
  scrollContainerRef,
  messagesEndRef,
  messageRefs,
  onResumeScrollToBottom,
  hasMessages,
  searchQuery,
  messages,
  onCitationClick,
  globalOrderedUuids,
  chatCitations,
  isLoading,
  isWaitingForDocumentProcessing,
  documentsByMessageId,
  promptAttachmentStatusById,
  onAttachedDocumentClick,
  canEditChat,
  onDraftClick,
  onEditPrompt,
  onExpandChatInput,
  documentNameById,
  clearsFloatingChatButton = false,
}: ChatScrollableMessageAreaProps) {
  const latestAssistantText = getLatestAssistantMessage(messages)?.content ?? '';
  const isStreamedTextIdle = useIsStreamedTextIdle(latestAssistantText, isLoading);
  // `null` while the steps row or the response text is already showing progress.
  const thinkingLabel = getChatThinkingLabel({
    isLoading,
    isWaitingForDocumentProcessing,
    messages,
    isStreamedTextIdle,
  });

  return (
    <div
      className={classNames('relative flex min-h-0 w-full flex-col', {
        'flex-1': hasMessages,
        'shrink-0': !hasMessages,
      })}
    >
      <div
        ref={scrollContainerRef}
        style={{
          maskImage: 'linear-gradient(to bottom, transparent 0, black 32px, black calc(100% - 32px), transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0, black 32px, black calc(100% - 32px), transparent 100%)',
        }}
        className={classNames(
          'hide-scrollbar flex min-h-0 w-full flex-col overflow-x-hidden overflow-y-auto px-4 @[480px]:px-12',
          {
            'flex-1': hasMessages,
            'shrink-0': !hasMessages,
          },
        )}
      >
        <div className="mx-auto flex w-full max-w-240 flex-col">
          <ChatMessageList
            messages={messages}
            searchQuery={searchQuery}
            messageRefs={messageRefs}
            onCitationClick={onCitationClick}
            globalOrderedUuids={globalOrderedUuids}
            chatCitations={chatCitations}
            documentsByMessageId={documentsByMessageId}
            promptAttachmentStatusById={promptAttachmentStatusById}
            isWaitingForDocumentProcessing={isWaitingForDocumentProcessing}
            isStreamingResponse={isLoading}
            onAttachedDocumentClick={onAttachedDocumentClick}
            canEditChat={canEditChat}
            onDraftClick={onDraftClick}
            onEditPrompt={onEditPrompt}
            onExpandChatInput={onExpandChatInput}
            documentNameById={documentNameById}
          />

          {thinkingLabel && (
            <div className="mb-6 flex w-full max-w-240 justify-start">
              <div className="flex w-fit justify-start">
                {/* Decorative: the label beside it carries the state, and
                    ChatStatusAnnouncer is what actually announces it. */}
                <span aria-hidden="true" className="inline-flex">
                  <ProgressSpinner unstyled style={{ width: 20, height: 20 }} strokeWidth="4" />
                </span>
                <div className="ml-2 text-sm font-semibold">{thinkingLabel}...</div>
              </div>
            </div>
          )}

          {clearsFloatingChatButton && <div className="h-12 w-full shrink-0 lg:h-16" aria-hidden />}
          <div ref={messagesEndRef} className="h-px w-full" aria-hidden />
        </div>
      </div>

      {/* Outside the scroller so its fade mask doesn't hide the button. */}
      {messages.length > 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center">
          <ScrollToRefButton
            refElem={messagesEndRef}
            scrollContainerRef={scrollContainerRef}
            messagesLength={messages.length}
            onResumeScroll={onResumeScrollToBottom}
          />
        </div>
      )}
    </div>
  );
}
